import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AdminScreen, LoadOrError, adminErrorText, subscriptionTone, useResource } from './Overview';
import {
  Alert, Button, Card, EmptyState, Icon, Input, Modal, Select, Spinner, StatusPill, Textarea, useToast
} from '../../components/ui';
import { api } from '../../lib/api';
import { brl, cycleLabel, datePt, dateTimePt, subscriptionLabel } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

type Cycle = 'monthly' | 'semiannual' | 'yearly';
type Method = 'pix' | 'cartao' | 'transferencia' | 'dinheiro' | 'outro';

const METHOD_LABEL: Record<Method, string> = {
  pix: 'Pix',
  cartao: 'Cartão',
  transferencia: 'Transferência bancária',
  dinheiro: 'Dinheiro',
  outro: 'Outro'
};

function methodLabel(method: Method): string {
  const label = METHOD_LABEL[method];
  return label ? tr(label) : method;
}

interface ActivationItem {
  businessId: string;
  businessName: string;
  slug: string;
  businessStatus: string;
  isPublic: number;
  createdAt: string;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string | null;
  planKey: string | null;
  planName: string | null;
  subscriptionStatus: string | null;
  billingCycle: string | null;
  provider: string | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  receivedCents: number;
  lastChargeAt: string | null;
  pendingPixId: string | null;
  pendingPixCents: number | null;
  pendingPixReference: string | null;
  pendingPixCreatedAt: string | null;
}

interface PlanPrice {
  key: string;
  name: string;
  monthlyCents: number;
  semiannualCents: number;
  yearlyCents: number;
}

interface AdminPix {
  enabled: boolean;
  key?: string;
  merchantName?: string;
  merchantCity?: string;
  planKey?: string;
  planName?: string;
  cycle?: Cycle;
  amountCents?: number;
  payload?: string;
  qrDataUrl?: string;
}

interface ActivationsPayload {
  items: ActivationItem[];
  plans: PlanPrice[];
}

interface ActivateResponse {
  subscription: { status: string; billingCycle: string; currentPeriodEnd: string | null };
  receipt: { id: string | null; amountCents: number; expectedCents: number; provider: string; alreadyRegistered: boolean };
}

const PENDING = new Set(['', 'trialing', 'past_due', 'expired', 'canceled', 'suspended']);

function priceFor(plan: PlanPrice | undefined, cycle: Cycle): number {
  if (!plan) return 0;
  return cycle === 'yearly' ? plan.yearlyCents : cycle === 'semiannual' ? plan.semiannualCents : plan.monthlyCents;
}

function toCents(reais: string): number | null {
  const clean = reais.replace(/[^\d.,]/g, '').replace(/\./g, '').replace(',', '.');
  const value = Number(clean);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

function displayReais(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

export function AdminActivationsPage(): JSX.Element {
  usePageMeta(tr('Ativações — administração'), tr('Fila de empreendedores e registro do recebimento que ativa a licença.'), '/admin/ativacoes');

  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('empresa');
  const [filter, setFilter] = useState<'pendentes' | 'ativas' | 'todas'>('pendentes');

  const list = useResource<ActivationsPayload>(() => api.get<ActivationsPayload>('/api/admin/activations'));

  const [pixData, setPixData] = useState<AdminPix | null>(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);

  async function copyPixPayload(payload: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(payload);
      setPixCopied(true);
      window.setTimeout(() => setPixCopied(false), 4000);
      toast(tr('Código Pix copiado. Cole no app do banco.'), 'success');
    } catch {
      toast(tr('Seu navegador bloqueou a cópia automática. Selecione o código e copie manualmente.'), 'info');
    }
  }

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const plans = list.data?.plans ?? [];

  const counts = useMemo(() => {
    const pending = items.filter((i) => PENDING.has(i.subscriptionStatus ?? '')).length;
    return { pendentes: pending, ativas: items.length - pending, todas: items.length };
  }, [items]);

  const rows = useMemo(() => {
    if (filter === 'ativas') return items.filter((i) => !PENDING.has(i.subscriptionStatus ?? ''));
    if (filter === 'pendentes') return items.filter((i) => PENDING.has(i.subscriptionStatus ?? ''));
    return items;
  }, [items, filter]);

  const selected = items.find((i) => i.businessId === selectedId) ?? null;

  // -------- formulário de recebimento --------
  const [planKey, setPlanKey] = useState<string>('');
  const [cycle, setCycle] = useState<Cycle>('semiannual');
  const [method, setMethod] = useState<Method>('pix');
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastReceipt, setLastReceipt] = useState<{ id: string; amountCents: number; alreadyRegistered: boolean } | null>(null);

  const activePlan = selected?.planKey ?? '';
  const effectivePlanKey = planKey || activePlan || plans[0]?.key || '';
  const chosenPlan = plans.find((p) => p.key === effectivePlanKey);
  const expectedCents = priceFor(chosenPlan, cycle);
  const amountCents = amount.trim() === '' ? expectedCents : toCents(amount);

  useEffect(() => {
    const key = chosenPlan?.key;
    if (!key) return;
    let cancelled = false;
    setPixLoading(true);
    api.get<AdminPix>(`/api/admin/pix?planKey=${encodeURIComponent(key)}&cycle=${cycle}`)
      .then((data) => {
        if (cancelled) return;
        setPixData(data);
        setPixCopied(false);
      })
      .catch(() => {
        if (!cancelled) setPixData(null);
      })
      .finally(() => {
        if (!cancelled) setPixLoading(false);
      });
    return () => { cancelled = true; };
  }, [chosenPlan?.key, cycle, expectedCents]);

  function openForm(item: ActivationItem): void {
    setError(null);
    setLastReceipt(null);
    setPlanKey(item.planKey ?? plans[0]?.key ?? '');
    setCycle((item.billingCycle as Cycle) || 'semiannual');
    if (item.pendingPixId) setMethod('pix');
    setReference(item.pendingPixReference ?? '');
    setAmount(item.pendingPixCents ? displayReais(Number(item.pendingPixCents)) : '');
    setNote('');
    setParams({ empresa: item.businessId });
  }

  async function submit(): Promise<void> {
    if (!selected) return;
    if (reference.trim().length < 3) {
      setError(tr('Informe o número do comprovante ou a referência do recebimento (mínimo de 3 caracteres).'));
      return;
    }
    if (amountCents === null) {
      setError(tr('Valor recebido inválido. Use números, por exemplo 161,46.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<ActivateResponse>(
        `/api/admin/businesses/${selected.businessId}/activate-license`,
        {
          planKey: chosenPlan?.key,
          cycle,
          method,
          reference: reference.trim(),
          amountCents,
          note: note.trim() || undefined
        }
      );
      setLastReceipt({
        id: String(res.receipt.id),
        amountCents: res.receipt.amountCents,
        alreadyRegistered: res.receipt.alreadyRegistered
      });
      toast(
        res.receipt.alreadyRegistered
          ? tr('Referência «{ref}» já estava registrada: nada foi cobrado nem prorrogado.', { ref: reference.trim() })
          : tr('Licença ativada. Recebimento de {amount} registrado em Auditoria.', { amount: brl(res.receipt.amountCents) }),
        res.receipt.alreadyRegistered ? 'info' : 'success'
      );
      setConfirmOpen(false);
      setReference('');
      setNote('');
      await list.reload();
    } catch (e) {
      setError(adminErrorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminScreen
      title={tr("Ativação de licenças")}
      description={tr("Clientes encontram os profissionais de graça. O empreendedor que quer o próprio perfil publicado assina um plano: você confere o comprovante, registra o recebimento e a licença entra em vigor — é isso que libera o painel e publica o perfil.")}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="nl-label mb-0">{tr("Fila")}</span>
        {(['pendentes', 'ativas', 'todas'] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`rounded-full border px-3 py-1 text-sm font-semibold ${
              filter === key ? 'border-transparent bg-accent text-on-accent shadow-[0_8px_16px_-10px_var(--nl-accent)]' : 'border-line bg-card text-muted hover:bg-surface'
            }`}
          >
            {key === 'pendentes' ? tr('Aguardando ativação') : key === 'ativas' ? tr('Licença ativa') : tr('Todos')}
            {` (${counts[key]})`}
          </button>
        ))}
        <Button variant="ghost" disabled={list.loading} onClick={() => void list.reload()}>
          {list.loading ? <Spinner label="" /> : <Icon name="chart" />} {tr("Atualizar")}
        </Button>
      </div>

      <LoadOrError loading={list.loading} error={list.error} />

      {plans.length > 0 ? (
        <Card className="mb-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-xl">{tr("Cobrança Pix do plano")}</h2>
              <p className="nl-hint mt-1">
                {tr("Escolha o plano e o ciclo: o QR e o copia e cola saem com o valor exato do plano, sem valor em aberto. Chave")} <strong>{pixData?.key ?? '—'}</strong> {tr("· recebedor")}{' '}
                <strong>{pixData?.merchantName ?? '—'}</strong> {tr("· cidade")} <strong>{pixData?.merchantCity ?? '—'}</strong>{tr(". Depois de receber, confira o comprovante e registre o recebimento ao lado.")}
              </p>
            </div>
            {pixLoading ? <Spinner label={tr("Gerando")} /> : null}
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="nl-label" htmlFor="pix-plan">{tr("Plano")}</label>
              <Select
                id="pix-plan"
                value={effectivePlanKey}
                onChange={(e) => { setPlanKey(e.target.value); setAmount(''); }}
              >
                {plans.map((p) => (
                  <option key={p.key} value={p.key}>{p.name} — {brl(p.monthlyCents)}{tr("/mês")}</option>
                ))}
              </Select>
            </div>

            <div>
              <span className="nl-label">{tr("Ciclo")}</span>
              <div className="inline-flex rounded-md border border-line p-0.5">
                {(['monthly', 'semiannual', 'yearly'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => { setCycle(option); setAmount(''); }}
                    className={`rounded-full px-3 py-1 text-sm font-semibold transition-all ${cycle === option ? 'bg-accent text-on-accent shadow-[0_8px_16px_-10px_var(--nl-accent)]' : 'text-muted hover:text-graphite'}`}
                  >
                    {cycleLabel(option)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {pixData?.enabled && pixData.payload && pixData.qrDataUrl ? (
            <div className="mt-4 grid gap-4 sm:grid-cols-[180px_minmax(0,1fr)]">
              <img
                src={pixData.qrDataUrl}
                alt={`QR Code Pix de ${brl(Number(pixData.amountCents ?? 0))} — ${pixData.planName}`}
                width={180}
                height={180}
                className="h-[180px] w-[180px] rounded-lg border border-line bg-white p-2"
              />
              <div>
                <label className="nl-label" htmlFor="admin-pix-payload">{tr("Pix copia e cola")}</label>
                <textarea
                  id="admin-pix-payload"
                  readOnly
                  value={pixData.payload}
                  rows={3}
                  className="nl-input w-full break-all font-mono text-xs"
                  aria-label={tr("Código Pix copia e cola")}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button onClick={() => void copyPixPayload(pixData.payload ?? '')}>
                    {pixCopied ? tr('Copiado') : tr('Copiar código Pix')}
                  </Button>
                  <span className="nl-hint">
                    {pixData.planName} · {cycleLabel(pixData.cycle ?? cycle)} {tr("— valor fixo de")}{' '}
                    {brl(Number(pixData.amountCents ?? 0))}{tr(". O cliente não digita nada no app do banco.")}
                  </span>
                </div>
              </div>
            </div>
          ) : pixData && !pixData.enabled ? (
            <div className="mt-4">
              <Alert tone="info" title={tr("Pix ainda não configurado")}>
                {tr("Preencha a chave Pix em")} <Link to="/admin/configuracoes" className="underline">{tr("Configurações")}</Link> {tr("para gerar o QR de recebimento.")}
              </Alert>
            </div>
          ) : null}
        </Card>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="grid gap-2">
          {rows.length === 0 && !list.loading && !list.error ? (
            <EmptyState
              title={filter === 'pendentes' ? tr('Ninguém aguardando ativação') : tr('Nenhum estabelecimento neste filtro')}
              description={tr("Quando um empreendedor terminar o teste ou solicitar um plano, ele aparece aqui para você registrar o recebimento.")}
            />
          ) : null}
          {rows.map((item) => {
            const status = item.subscriptionStatus ?? '';
            const venceEm = item.currentPeriodEnd ?? item.trialEndsAt;
            return (
              <button
                key={item.businessId}
                type="button"
                onClick={() => openForm(item)}
                className={`rounded-lg border p-4 text-left transition-colors ${
                  selectedId === item.businessId ? 'border-accent bg-surface' : 'border-line bg-card hover:bg-surface'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold">{item.businessName}</p>
                  <StatusPill tone={subscriptionTone(status || null)}>
                    {status ? subscriptionLabel(status) : tr('Sem licença')}
                  </StatusPill>
                </div>
                <p className="nl-hint mt-1">
                  {item.ownerName} · {item.ownerEmail}{item.ownerPhone ? ` · ${item.ownerPhone}` : ''}
                </p>
                <p className="nl-hint mt-1">
                  {item.planName ? `Plano ${item.planName}` : 'Nenhum plano contratado'}
                  {item.billingCycle ? tr(' · ciclo {cycle}', { cycle: cycleLabel(item.billingCycle) }) : ''}
                  {venceEm ? tr(' · vence em {date}', { date: datePt(venceEm) }) : ''}
                  {tr(" · recebido ")}
                  {brl(Number(item.receivedCents))}
                  {item.lastChargeAt ? tr(' (último em {date})', { date: datePt(item.lastChargeAt) }) : ''}
                </p>
                {item.pendingPixId ? (
                  <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-sm">
                    <StatusPill tone="warning">{tr("Pix gerado")}</StatusPill>
                    <span className="nl-hint">
                      {brl(Number(item.pendingPixCents))} {tr("· ref.")} {item.pendingPixReference} {tr("· emitido em")}{' '}
                      {item.pendingPixCreatedAt ? dateTimePt(item.pendingPixCreatedAt) : '—'}
                    </span>
                  </p>
                ) : null}
              </button>
            );
          })}
        </div>

        <div>
          {!selected ? (
            <Card>
              <h2 className="text-xl">{tr("Nenhum empreendedor selecionado")}</h2>
              <p className="nl-hint mt-1">
                {tr("Escolha um estabelecimento à esquerda para registrar o recebimento e ativar a licença. Toda ativação fica no histórico de cobrança e em Auditoria.")}
              </p>
            </Card>
          ) : (
            <Card>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl">{selected.businessName}</h2>
                  <p className="nl-hint">
                    {selected.ownerName} · <Link to={`/profissional/${selected.slug}`} className="text-graphite underline">{selected.slug}</Link>
                  </p>
                </div>
                <StatusPill tone={subscriptionTone(selected.subscriptionStatus)}>
                  {selected.subscriptionStatus ? subscriptionLabel(selected.subscriptionStatus) : 'Sem licença'}
                </StatusPill>
              </div>

              {selected.businessStatus === 'suspended' ? (
                <div className="mt-3">
                  <Alert tone="danger" title={tr("Conta suspensa")}>
                    {tr("Reative o estabelecimento em")} <Link to="/admin/empresas" className="underline">{tr("Empresas")}</Link> {tr("antes de registrar uma licença.")}
                  </Alert>
                </div>
              ) : null}

              {lastReceipt ? (
                <div className="mt-3">
                  <Alert tone={lastReceipt.alreadyRegistered ? 'info' : 'success'} title={lastReceipt.alreadyRegistered ? tr('Referência repetida') : tr('Licença ativada')}>
                    {lastReceipt.alreadyRegistered
                      ? tr('O comprovante informado já constava no histórico ({amount}). Nada foi cobrado e o período não avançou.', { amount: brl(lastReceipt.amountCents) })
                      : tr('Recebimento de {amount} lançado (transação {id}).', { amount: brl(lastReceipt.amountCents), id: lastReceipt.id })}
                  </Alert>
                </div>
              ) : null}

              <div className="mt-4 grid gap-3">
                <div className="rounded-md border border-line bg-surface px-3 py-2">
                  <p className="text-sm font-semibold">
                    {chosenPlan ? `${chosenPlan.name} · ${cycleLabel(cycle)}` : 'Nenhum plano selecionado'}
                  </p>
                  <p className="nl-hint mt-0.5">
                    {tr("Plano e ciclo definidos na")} <strong>{tr("Cobrança Pix do plano")}</strong>{tr(", logo acima — tabela de")}{' '}
                    {brl(expectedCents)}{tr(". Troque lá para registrar outro plano ou ciclo.")}
                  </p>
                </div>

                <div>
                  <label className="nl-label" htmlFor="act-method">{tr("Forma de recebimento")}</label>
                  <Select id="act-method" value={method} onChange={(e) => setMethod(e.target.value as Method)}>
                    {(Object.keys(METHOD_LABEL) as Method[]).map((m) => (
                      <option key={m} value={m}>{methodLabel(m)}</option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label className="nl-label" htmlFor="act-reference">{tr("Comprovante / referência")}</label>
                  <Input
                    id="act-reference"
                    value={reference}
                    maxLength={120}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder={tr("Ex.: Pix 20261021-8842 ou comprovante nº 0123")}
                  />
                  <p className="nl-hint mt-1">
                    {tr("Obrigatório. Se você registrar a mesma referência duas vezes, a segunda é ignorada: nada é cobrado e o período não avança.")}
                  </p>
                </div>

                <div>
                  <label className="nl-label" htmlFor="act-amount">{tr("Valor recebido (R$)")}</label>
                  <Input
                    id="act-amount"
                    inputMode="decimal"
                    value={amount === '' ? displayReais(expectedCents) : amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  <p className="nl-hint mt-1">
                    {tr("Pré-preenchido com a tabela. Ajuste somente se o combinado for diferente e registre o motivo.")}
                  </p>
                </div>

                <div>
                  <label className="nl-label" htmlFor="act-note">{tr("Observação (opcional)")}</label>
                  <Textarea
                    id="act-note"
                    rows={2}
                    maxLength={300}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={tr("Ex.: combinado 6 meses com desconto de primeiro contrato, negociado por WhatsApp.")}
                  />
                </div>

                {error ? <Alert tone="danger" title={tr("Não foi possível ativar")}>{error}</Alert> : null}

                <Button disabled={busy || !chosenPlan} onClick={() => { setError(null); setConfirmOpen(true); }}>
                  {tr("Registrar recebimento e ativar")}
                </Button>
                <p className="nl-hint">
                  {tr("Histórico e eventos completos desta empresa em")}{' '}
                  <Link to={`/admin/assinaturas?empresa=${encodeURIComponent(selected.businessId)}`} className="underline">
                    {tr("Assinaturas")}
                  </Link>.
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => (busy ? undefined : setConfirmOpen(false))}
        title={tr("Confirmar recebimento e ativar a licença")}
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setConfirmOpen(false)}>{tr("Voltar")}</Button>
            <Button loading={busy} disabled={busy} onClick={() => void submit()}>{tr("Confirmar registro")}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Alert tone="warning" title={tr("Isso libera o painel do cliente")}>
            {tr("Você está registrando")} <strong>{brl(amountCents ?? 0)}</strong> {tr("como recebido de")}{' '}
            <strong>{selected?.businessName}</strong> ({cycleLabel(cycle).toLowerCase()} {tr("do plano")} {chosenPlan?.name}{tr("), via")}{' '}
            {methodLabel(method)}{tr(", comprovante")} <strong>{reference.trim() || '—'}</strong>{tr(". A licença entra em vigor pelo ciclo escolhido e o perfil volta a aparecer na busca. Nada é cobrado automaticamente: este lançamento representa dinheiro que já entrou.")}
          </Alert>
          <p className="nl-hint">
            {tr("A operação entra no histórico da empresa (")}<code>{tr("payment_transactions")}</code>{tr("), em")}{' '}
            <code>{tr("admin.license_activated")}</code> {tr("e em Auditoria com a sua identificação.")}
          </p>
        </div>
      </Modal>
    </AdminScreen>
  );
}
